import React, { useEffect, useRef, useState } from 'react';
import { FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import folderService from '../../services/folderService';
import './OnlyOfficeViewerModal.css';

const SCRIPT_ID = 'onlyoffice-docs-api';

/**
 * Detects the OnlyOffice documentType from a file extension.
 * Returns 'word' | 'cell' | 'slide' | null
 */
const detectDocumentType = (ext) => {
    const e = (ext || '').toLowerCase();
    if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(e)) return 'word';
    if (['xls', 'xlsx', 'ods', 'csv'].includes(e))           return 'cell';
    if (['ppt', 'pptx', 'odp'].includes(e))                  return 'slide';
    return null;
};

const OnlyOfficeViewerModal = ({ file, onClose }) => {
    const editorRef      = useRef(null);
    const containerRef   = useRef(null);
    const wasModifiedRef = useRef(false);
    const destroyTimerRef = useRef(null);
    const [status, setStatus]     = useState('loading');
    const [errorMsg, setErrorMsg] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!file) return;

        // Reset state for each new file
        setStatus('loading');
        setErrorMsg('');

        let cancelled = false;
        let docEditor  = null;

        const cleanup = () => {
            cancelled = true;
            // Cancel any pending delayed-destroy timer
            if (destroyTimerRef.current) clearTimeout(destroyTimerRef.current);
            if (docEditor) {
                // forceSave tells OnlyOffice to POST the saved file to callbackUrl (status=6).
                // We MUST NOT call destroyEditor() immediately — doing so races with the async
                // HTTP callback and can cancel the save before our backend processes it.
                // Give OnlyOffice 2 s to finish the save, then destroy.
                try {
                    if (wasModifiedRef.current) {
                        docEditor.forceSave();
                    }
                } catch (_) {}
                const editorToDestroy = docEditor;
                docEditor = null;
                destroyTimerRef.current = setTimeout(() => {
                    try { editorToDestroy.destroyEditor(); } catch (_) {}
                }, wasModifiedRef.current ? 2000 : 0);
            }
            const existingScript = document.getElementById(SCRIPT_ID);
            if (existingScript) existingScript.remove();
        };

        const init = async () => {
            try {
                // 1. Fetch config from backend
                const res = await folderService.getOnlyOfficeConfig(file.id);
                if (cancelled) return;

                if (!res?.status) {
                    setErrorMsg('Failed to load document configuration.');
                    setStatus('error');
                    return;
                }

                const config  = res.data;
                const docType = detectDocumentType(config.fileType);
                if (!docType) {
                    setErrorMsg(`Unsupported file type: .${config.fileType}`);
                    setStatus('error');
                    return;
                }

                // 2. Load DocsAPI script from OnlyOffice server
                const serverUrl = process.env.REACT_APP_ONLYOFFICE_SERVER_URL || 'http://localhost:8081';
                const scriptSrc = `${serverUrl}/web-apps/apps/api/documents/api.js`;

                await new Promise((resolve, reject) => {
                    if (document.getElementById(SCRIPT_ID)) { resolve(); return; }
                    const script    = document.createElement('script');
                    script.id       = SCRIPT_ID;
                    script.src      = scriptSrc;
                    script.onload   = resolve;
                    script.onerror  = () => reject(new Error('Failed to load OnlyOffice DocsAPI script. Is the server running?'));
                    document.head.appendChild(script);
                });

                if (cancelled) return;
                if (!window.DocsAPI) throw new Error('DocsAPI not available after script load.');

                // 3. Initialise the editor (edit or view mode based on ownership)
                const isOwner = config.permissions?.is_owner || false;
                const editorMode = isOwner ? 'edit' : 'view';
                docEditor = new window.DocsAPI.DocEditor('onlyoffice-editor-container', {
                    document: {
                        fileType   : config.fileType,
                        key        : config.key,
                        title      : config.title,
                        url        : config.documentUrl,
                        permissions: { edit: isOwner, download: true },
                    },
                    editorConfig: {
                        mode: editorMode,
                        ...(isOwner && config.callbackUrl && { callbackUrl: config.callbackUrl }),
                        customization: {
                            // autosave: true — OnlyOffice will POST to callbackUrl (status=6)
                            // periodically while the user edits, keeping the backend file current.
                            // This is the critical fix: without it the backend file is never updated
                            // while the editor is open, so any download serves the original.
                            autosave: isOwner && !!config.callbackUrl,
                            forcesave: isOwner && !!config.callbackUrl,
                            hideRightMenu: true,
                            features: {
                                tabStyle: 'line',
                                tabBackground: 'white',
                            },
                        },
                    },
                    documentType: docType,
                    height: '100%',
                    width : '100%',
                    events: {
                        onAppReady: () => { if (!cancelled) setStatus('ready'); },
                        onDocumentStateChange: (event) => {
                            // event.data = true means unsaved changes exist
                            if (event?.data === true) wasModifiedRef.current = true;
                        },
                        onRequestSaveAs: () => {
                            // Fired when the user clicks the built-in Save/ForceSave button
                            if (!cancelled) setIsSaving(true);
                        },
                        onSave: () => {
                            // Fired after a successful save cycle completes
                            if (!cancelled) setIsSaving(false);
                        },
                        onError: (event) => {
                            if (!cancelled) {
                                setIsSaving(false);
                                setErrorMsg(event?.data?.errorDescription || 'An error occurred in the document editor.');
                                setStatus('error');
                            }
                        },
                    },
                });

                editorRef.current = docEditor;
            } catch (err) {
                if (!cancelled) {
                    setErrorMsg(err.message || 'Unexpected error loading document.');
                    setStatus('error');
                }
            }
        };

        init();
        return cleanup;
    }, [file]);

    const handleClose = () => {
        if (isSaving) return; // block close while an autosave is in flight
        onClose(wasModifiedRef.current);
    };

    // Close on Escape key (only when open)
    useEffect(() => {
        if (!file) return;
        const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [file, onClose]);

    if (!file) return null;

    const title = file.original_filename || file.name || 'Document';

    // No portal needed — position:fixed already escapes any stacking context,
    // avoiding the insertBefore conflict with other portals in the tree.
    return (
        <div
            className="oo-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
            role="dialog"
            aria-modal="true"
            aria-label={`Viewing ${title}`}
        >
            <div className="oo-modal">
                {/* Header */}
                <div className="oo-header">
                    <span className="oo-title" title={title}>{title}</span>
                    {isSaving && (
                        <span className="oo-saving-indicator" aria-live="polite">
                            Saving…
                        </span>
                    )}
                    <button
                        id="onlyoffice-close-btn"
                        className="oo-close-btn"
                        onClick={handleClose}
                        disabled={isSaving}
                        aria-label="Close document viewer"
                        title={isSaving ? 'Saving changes, please wait…' : 'Close'}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className="oo-body" ref={containerRef}>
                    {/* Editor mount target — always in DOM to avoid insertBefore conflicts */}
                    <div
                        id="onlyoffice-editor-container"
                        className="oo-editor-container"
                        style={{ visibility: status === 'ready' ? 'visible' : 'hidden' }}
                    />

                    {/* State overlays positioned absolutely on top */}
                    {status === 'loading' && (
                        <div className="oo-state-overlay">
                            <FaSpinner className="oo-spinner" />
                            <p>Loading document…</p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="oo-state-overlay oo-error">
                            <FaExclamationTriangle className="oo-error-icon" />
                            <p className="oo-error-msg">{errorMsg}</p>
                            <button className="oo-retry-btn" onClick={handleClose}>Close</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnlyOfficeViewerModal;
